/**
 * Color Contrast Helpers (Story 14.5 — moved from apps/web/src/lib/color-contrast.ts)
 *
 * Shared WCAG 2.1 relative-luminance helpers used by both the portal shell
 * (to pick a contrasting text color at render time) and the Branding settings
 * UI (to warn an agency when their chosen primary color fails 4.5:1 contrast
 * against default text).
 *
 * Single source of truth lives in @provenleap/types so the server-side PATCH
 * validator in /api/tenant/branding and the web-side ContrastBadge component
 * agree on the ratios down to the last decimal.
 *
 * Simple sRGB luminance (not OKLCH) — sufficient for the narrow use case, no
 * color-library dependency on the portal bundle which loads without auth.
 *
 * WCAG 2.1 spec: https://www.w3.org/WAI/GL/wiki/Relative_luminance
 */

/** Text color options returned by `getContrastingText`. */
export type ContrastingTextColor = "#111827" | "#ffffff";

/**
 * Parse a 3- or 6-digit hex color string into normalized sRGB components in [0,1].
 * Returns null on invalid input — caller should fall back to the default theme.
 */
function parseHex(hex: string): [number, number, number] | null {
  let h = hex.trim();
  if (h.startsWith("#")) h = h.slice(1);
  if (h.length === 3) {
    h = h
      .split("")
      .map((c) => c + c)
      .join("");
  }
  if (h.length !== 6) return null;
  if (!/^[0-9a-fA-F]{6}$/.test(h)) return null;
  const r = parseInt(h.slice(0, 2), 16) / 255;
  const g = parseInt(h.slice(2, 4), 16) / 255;
  const b = parseInt(h.slice(4, 6), 16) / 255;
  return [r, g, b];
}

/**
 * Compute WCAG 2.1 relative luminance for an sRGB triplet in [0,1].
 */
function srgbLuminance(r: number, g: number, b: number): number {
  const channel = (c: number): number =>
    c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  return (
    0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b)
  );
}

/**
 * Pick a text color (`#111827` deep gray or `#ffffff` white) that achieves
 * ≥ 4.5:1 WCAG AA contrast against the given background hex color.
 *
 * Algorithm:
 *   1. Parse hex to sRGB; on invalid input, return the default deep gray.
 *   2. Compute luminance L of the background.
 *   3. Compute contrast against white (1.0) and against deep gray (~0.0198).
 *   4. Prefer whichever is higher; break ties toward `#111827` (matches the
 *      default theme, so unstyled fallbacks stay consistent).
 */
export function getContrastingText(hex: string | null | undefined): ContrastingTextColor {
  const DEFAULT: ContrastingTextColor = "#111827";
  if (!hex) return DEFAULT;

  const parsed = parseHex(hex);
  if (!parsed) return DEFAULT;

  const bgL = srgbLuminance(parsed[0], parsed[1], parsed[2]);

  // Deep gray luminance (approximate — constant for #111827)
  const deepL = srgbLuminance(17 / 255, 24 / 255, 39 / 255);
  const whiteL = 1;

  const contrastWithWhite =
    (Math.max(bgL, whiteL) + 0.05) / (Math.min(bgL, whiteL) + 0.05);
  const contrastWithDeep =
    (Math.max(bgL, deepL) + 0.05) / (Math.min(bgL, deepL) + 0.05);

  // Prefer deep gray when both meet AA or neither does (consistent with theme)
  if (contrastWithDeep >= 4.5 && contrastWithDeep >= contrastWithWhite) {
    return "#111827";
  }
  if (contrastWithWhite >= 4.5) {
    return "#ffffff";
  }
  // Neither reaches 4.5:1 (rare — very saturated mid-tones). Pick whichever
  // is higher so we maximize legibility rather than flipping to an arbitrary
  // default.
  return contrastWithDeep >= contrastWithWhite ? "#111827" : "#ffffff";
}

/**
 * Compute the WCAG 2.1 contrast ratio between two hex colors.
 *
 * Returns the ratio rounded to two decimal places (e.g., 4.51, 21.00).
 * Invalid input returns 1 (minimum possible ratio — caller can detect with
 * `< 4.5` WCAG AA threshold).
 *
 * @param fgHex — foreground / text hex (3- or 6-digit, with or without #)
 * @param bgHex — background hex (3- or 6-digit, with or without #)
 */
export function computeContrastRatio(
  fgHex: string | null | undefined,
  bgHex: string | null | undefined,
): number {
  if (!fgHex || !bgHex) return 1;
  const fg = parseHex(fgHex);
  const bg = parseHex(bgHex);
  if (!fg || !bg) return 1;

  const fgL = srgbLuminance(fg[0], fg[1], fg[2]);
  const bgL = srgbLuminance(bg[0], bg[1], bg[2]);
  const ratio = (Math.max(fgL, bgL) + 0.05) / (Math.min(fgL, bgL) + 0.05);
  return Math.round(ratio * 100) / 100;
}
